import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, type Me } from './lib/api';
import { importLocalAnswersToServer } from './lib/answerStore';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import DashboardPage from './pages/DashboardPage';
import LessonPage from './pages/LessonPage';
import WelcomePage from './pages/WelcomePage';
import ProfilePage from './pages/ProfilePage';
import LandingPage from './pages/LandingPage';
import RefundsPage from './pages/RefundsPage';
import LessonPreviewPage from './pages/LessonPreviewPage';
import LessonPreviewPage1_2 from './pages/LessonPreviewPage1_2';
import LessonPreviewPage1_3 from './pages/LessonPreviewPage1_3';
import LessonPreviewPage1_4 from './pages/LessonPreviewPage1_4';
import LessonPreviewPage1_5 from './pages/LessonPreviewPage1_5';
import LessonPreviewPage2_1 from './pages/LessonPreviewPage2_1';
import LessonPreviewPage2_2 from './pages/LessonPreviewPage2_2';
import LessonPreviewPage2_3 from './pages/LessonPreviewPage2_3';
import LessonPreviewPage2_4 from './pages/LessonPreviewPage2_4';
import LessonPreviewPage2_5 from './pages/LessonPreviewPage2_5';
import LessonPreviewPage3_1 from './pages/LessonPreviewPage3_1';
import LessonPreviewPage3_2 from './pages/LessonPreviewPage3_2';
import LessonPreviewPage3_3 from './pages/LessonPreviewPage3_3';
import LessonPreviewPage3_4 from './pages/LessonPreviewPage3_4';
import LessonPreviewPage3_5 from './pages/LessonPreviewPage3_5';
import LessonPreviewPage4_1 from './pages/LessonPreviewPage4_1';
import LessonPreviewPage4_2 from './pages/LessonPreviewPage4_2';
import LessonPreviewPage4_3 from './pages/LessonPreviewPage4_3';
import LessonPreviewPage4_4 from './pages/LessonPreviewPage4_4';
import LessonPreviewPage4_5 from './pages/LessonPreviewPage4_5';
import LessonPreviewPage5_1 from './pages/LessonPreviewPage5_1';
import LessonPreviewPage5_2 from './pages/LessonPreviewPage5_2';
import LessonPreviewPage5_3 from './pages/LessonPreviewPage5_3';
import LessonPreviewPage5_4 from './pages/LessonPreviewPage5_4';
import LessonPreviewPage5_5 from './pages/LessonPreviewPage5_5';
import LessonPreviewPage6_1 from './pages/LessonPreviewPage6_1';
import LessonPreviewPage7_1 from './pages/LessonPreviewPage7_1';
import LessonPreviewPage7_2 from './pages/LessonPreviewPage7_2';
import LessonPreviewPage7_3 from './pages/LessonPreviewPage7_3';
import LessonPreviewPage7_4 from './pages/LessonPreviewPage7_4';
import LessonPreviewPage7_5 from './pages/LessonPreviewPage7_5';
import LessonPreviewPage8_1 from './pages/LessonPreviewPage8_1';
import LessonPreviewPage8_2 from './pages/LessonPreviewPage8_2';
import LessonPreviewPage8_3 from './pages/LessonPreviewPage8_3';
import LessonPreviewPage8_4 from './pages/LessonPreviewPage8_4';
import LessonPreviewPage8_5 from './pages/LessonPreviewPage8_5';
import GatedLesson from './components/GatedLesson';

export type User = NonNullable<Me['user']>;

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function refreshUser() {
    // On the static preview deploy there is no backend. Skip the auth call so
    // the app loads instantly into landing + /preview/* routes.
    if (import.meta.env.VITE_STATIC_PREVIEW === '1') {
      setUser(null);
      setLoaded(true);
      return;
    }
    try {
      const me = await api.get<Me>('/api/auth/me');
      setUser(me.user);
    } catch {
      setUser(null);
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void refreshUser();
  }, []);

  // One-time sweep: after auth, migrate any localStorage answers the student
  // wrote in the preview build up to Turso. Idempotent; skipped in preview.
  useEffect(() => {
    if (!user) return;
    void importLocalAnswersToServer();
  }, [user]);

  if (!loaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <Layout user={user} onLogout={() => setUser(null)}>
      <Routes>
        <Route path="/" element={<LandingPage user={user} />} />
        <Route path="/preview/lesson/1.1" element={<GatedLesson user={user}><LessonPreviewPage /></GatedLesson>} />
        <Route path="/preview/lesson/1.2" element={<GatedLesson user={user}><LessonPreviewPage1_2 /></GatedLesson>} />
        <Route path="/preview/lesson/1.3" element={<GatedLesson user={user}><LessonPreviewPage1_3 /></GatedLesson>} />
        <Route path="/preview/lesson/1.4" element={<GatedLesson user={user}><LessonPreviewPage1_4 /></GatedLesson>} />
        <Route path="/preview/lesson/1.5" element={<GatedLesson user={user}><LessonPreviewPage1_5 /></GatedLesson>} />
        <Route path="/preview/lesson/2.1" element={<GatedLesson user={user}><LessonPreviewPage2_1 /></GatedLesson>} />
        <Route path="/preview/lesson/2.2" element={<GatedLesson user={user}><LessonPreviewPage2_2 /></GatedLesson>} />
        <Route path="/preview/lesson/2.3" element={<GatedLesson user={user}><LessonPreviewPage2_3 /></GatedLesson>} />
        <Route path="/preview/lesson/2.4" element={<GatedLesson user={user}><LessonPreviewPage2_4 /></GatedLesson>} />
        <Route path="/preview/lesson/2.5" element={<GatedLesson user={user}><LessonPreviewPage2_5 /></GatedLesson>} />
        <Route path="/preview/lesson/3.1" element={<GatedLesson user={user}><LessonPreviewPage3_1 /></GatedLesson>} />
        <Route path="/preview/lesson/3.2" element={<GatedLesson user={user}><LessonPreviewPage3_2 /></GatedLesson>} />
        <Route path="/preview/lesson/3.3" element={<GatedLesson user={user}><LessonPreviewPage3_3 /></GatedLesson>} />
        <Route path="/preview/lesson/3.4" element={<GatedLesson user={user}><LessonPreviewPage3_4 /></GatedLesson>} />
        <Route path="/preview/lesson/3.5" element={<GatedLesson user={user}><LessonPreviewPage3_5 /></GatedLesson>} />
        <Route path="/preview/lesson/4.1" element={<GatedLesson user={user}><LessonPreviewPage4_1 /></GatedLesson>} />
        <Route path="/preview/lesson/4.2" element={<GatedLesson user={user}><LessonPreviewPage4_2 /></GatedLesson>} />
        <Route path="/preview/lesson/4.3" element={<GatedLesson user={user}><LessonPreviewPage4_3 /></GatedLesson>} />
        <Route path="/preview/lesson/4.4" element={<GatedLesson user={user}><LessonPreviewPage4_4 /></GatedLesson>} />
        <Route path="/preview/lesson/4.5" element={<GatedLesson user={user}><LessonPreviewPage4_5 /></GatedLesson>} />
        <Route path="/preview/lesson/5.1" element={<GatedLesson user={user}><LessonPreviewPage5_1 /></GatedLesson>} />
        <Route path="/preview/lesson/5.2" element={<GatedLesson user={user}><LessonPreviewPage5_2 /></GatedLesson>} />
        <Route path="/preview/lesson/5.3" element={<GatedLesson user={user}><LessonPreviewPage5_3 /></GatedLesson>} />
        <Route path="/preview/lesson/5.4" element={<GatedLesson user={user}><LessonPreviewPage5_4 /></GatedLesson>} />
        <Route path="/preview/lesson/5.5" element={<GatedLesson user={user}><LessonPreviewPage5_5 /></GatedLesson>} />
        <Route path="/preview/lesson/6.1" element={<GatedLesson user={user}><LessonPreviewPage6_1 /></GatedLesson>} />
        <Route path="/preview/lesson/7.1" element={<GatedLesson user={user}><LessonPreviewPage7_1 /></GatedLesson>} />
        <Route path="/preview/lesson/7.2" element={<GatedLesson user={user}><LessonPreviewPage7_2 /></GatedLesson>} />
        <Route path="/preview/lesson/7.3" element={<GatedLesson user={user}><LessonPreviewPage7_3 /></GatedLesson>} />
        <Route path="/preview/lesson/7.4" element={<GatedLesson user={user}><LessonPreviewPage7_4 /></GatedLesson>} />
        <Route path="/preview/lesson/7.5" element={<GatedLesson user={user}><LessonPreviewPage7_5 /></GatedLesson>} />
        <Route path="/preview/lesson/8.1" element={<GatedLesson user={user}><LessonPreviewPage8_1 /></GatedLesson>} />
        <Route path="/preview/lesson/8.2" element={<GatedLesson user={user}><LessonPreviewPage8_2 /></GatedLesson>} />
        <Route path="/preview/lesson/8.3" element={<GatedLesson user={user}><LessonPreviewPage8_3 /></GatedLesson>} />
        <Route path="/preview/lesson/8.4" element={<GatedLesson user={user}><LessonPreviewPage8_4 /></GatedLesson>} />
        <Route path="/preview/lesson/8.5" element={<GatedLesson user={user}><LessonPreviewPage8_5 /></GatedLesson>} />
        <Route path="/login" element={<LoginPage onAuth={refreshUser} />} />
        <Route path="/verify" element={<VerifyPage onAuth={refreshUser} />} />
        <Route path="/welcome" element={<WelcomePage onAuth={refreshUser} />} />
        <Route path="/refunds" element={<RefundsPage />} />
        <Route
          path="/dashboard"
          element={
            <RequireActive user={user}>
              <DashboardPage />
            </RequireActive>
          }
        />
        <Route
          path="/lesson/:slug"
          element={
            <RequireActive user={user}>
              <LessonPage />
            </RequireActive>
          }
        />
        <Route
          path="/profile"
          element={
            user ? <ProfilePage /> : <Navigate to="/login?next=/profile" replace />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function RequireActive({ user, children }: { user: User | null; children: React.ReactNode }) {
  const loc = useLocation();
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (user.status !== 'active') return <Navigate to="/" replace />;
  return <>{children}</>;
}
